import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { AlertTriangle, Info } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { translate } from '@/lib/i18n/store';

// ============================================================================
// dialogs.tsx — глобальные диалоги вместо alert/confirm (стандартные виджеты)
// ============================================================================
// confirmDialog(message, title?) → Promise<boolean> — подтверждение
// alertDialog(message, title?) → Promise<void> — уведомление
// Компактная центрированная карточка: заголовок, сообщение, кнопки.

interface DialogState {
  open: boolean;
  title: string;
  message: string;
  kind: 'confirm' | 'alert' | 'prompt';
  defaultValue?: string;
  placeholder?: string;
  /** Подписи кнопок подтверждения/отказа (по умолчанию «Да»/«Отмена»). */
  confirmText?: string;
  cancelText?: string;
  resolve?: (value: boolean) => void;
  resolveText?: (value: string | null) => void;
}

export interface ConfirmDialogOptions {
  /** Текст кнопки подтверждения (по умолчанию «Да»). */
  confirmText?: string;
  /** Текст кнопки отказа (по умолчанию «Отмена»). */
  cancelText?: string;
}

let setDialogState: ((s: DialogState) => void) | null = null;

function openDialog(kind: 'confirm' | 'alert', message: string, title: string, opts?: ConfirmDialogOptions): Promise<boolean> {
  return new Promise((resolve) => {
    setDialogState?.({
      open: true,
      title,
      message,
      kind,
      resolve,
      confirmText: opts?.confirmText,
      cancelText: opts?.cancelText,
    });
  });
}

/**
 * confirmDialog — подтверждение. Подписи кнопок можно задать явно
 * (например, «Заменить»/«Оставить»); по умолчанию — «Да»/«Отмена».
 */
export function confirmDialog(message: string, title = translate('dialog.title.confirm'), opts?: ConfirmDialogOptions): Promise<boolean> {
  return openDialog('confirm', message, title, opts);
}

export function alertDialog(message: string, title = translate('dialog.title.message')): Promise<void> {
  return openDialog('alert', message, title).then(() => undefined);
}

/** Диалог ВВОДА (одно текстовое поле). Возвращает строку; null — отмена. */
export function promptDialog(message: string, defaultValue = '', title = translate('dialog.title.input'), placeholder = ''): Promise<string | null> {
  return new Promise((resolve) => {
    setDialogState?.({ open: true, title, message, kind: 'prompt', defaultValue, placeholder, resolveText: resolve });
  });
}

// Глобальный контейнер диалогов (рендерится в App рядом с ToastContainer)
export function ConfirmDialogContainer() {
  const [state, setState] = useState<DialogState>({ open: false, title: '', message: '', kind: 'alert' });
  const [text, setText] = useState('');
  setDialogState = setState;

  // Инициализация поля ввода значением по умолчанию при открытии prompt
  useEffect(() => {
    if (state.open && state.kind === 'prompt') setText(state.defaultValue || '');
  }, [state.open, state.kind, state.defaultValue]);

  const close = (result: boolean) => {
    state.resolve?.(result);
    state.resolveText?.(result ? text : null);
    setState((prev) => ({ ...prev, open: false }));
  };

  if (!state.open) return null;

  const isConfirm = state.kind === 'confirm';
  const isPrompt = state.kind === 'prompt';

  // Портал в document.body + z-index ВЫШЕ модальных окон UniversalWindow (15000),
  // иначе диалог подтверждения/комментария оказывается ПОД формой объекта
  // (например, под формой задачи). Слой 20000 — над формой, но под тостами.
  return createPortal(
    <div className="fixed inset-0 z-[20000] flex items-center justify-center bg-black/40" onClick={() => close(false)}>
      <div
        className="w-[380px] max-w-[90vw] bg-background border rounded-xl shadow-2xl overflow-hidden"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Заголовок */}
        <div className="flex items-center gap-2.5 px-4 py-3 border-b bg-muted/30">
          {isConfirm ? (
            <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" />
          ) : (
            <Info className="h-4 w-4 text-amber-500 shrink-0" />
          )}
          <span className="text-sm font-medium">{state.title}</span>
        </div>
        {/* Сообщение */}
        <div className="px-4 py-3.5 text-sm whitespace-pre-wrap break-words leading-relaxed">{state.message}</div>
        {/* Поле ввода (prompt) */}
        {isPrompt && (
          <div className="px-4 pb-3">
            <input
              autoFocus
              className="w-full h-9 rounded-md border bg-background px-2 text-sm outline-none focus:ring-2 focus:ring-ring"
              value={text}
              placeholder={state.placeholder}
              onChange={(e) => setText(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') close(true); if (e.key === 'Escape') close(false); }}
            />
          </div>
        )}
        {/* Кнопки */}
        <div className="flex justify-end gap-2 px-4 py-3 border-t bg-muted/20">
          {(isConfirm || isPrompt) && (
            <Button variant="outline" size="sm" onClick={() => close(false)}>
              {state.cancelText || translate('dialog.cancel')}
            </Button>
          )}
          <Button
            size="sm"
            variant={isConfirm ? 'default' : 'default'}
            className={isConfirm ? '' : ''}
            onClick={() => close(true)}
            autoFocus={!isPrompt}
          >
            {isConfirm ? (state.confirmText || translate('dialog.yes')) : translate('dialog.ok')}
          </Button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
